import React, { useState, useEffect, useRef, useCallback } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion, AnimatePresence } from "framer-motion";
import {
  Volume2,
  Trash2,
  Plus,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Delete,
  Mail,
  Pencil,
  Check,
  RotateCcw,
  Undo2,
  Settings2,
  Keyboard,
} from "lucide-react";
import { useInputMethod } from "@/contexts/InputMethodContext";
import { InputMethod } from "@/lib/inputMethods";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { speak, stopSpeaking } from "@/lib/ttsService";
import { getUserSettings } from "@/lib/userSettings";
import { renderIconByName } from "@/components/communication/IconPicker";
import { getColorClasses } from "@/components/communication/ColorPicker";
import { loadCommunicationActionBarConfig, CommunicationActionBarConfig } from "@/lib/communicationActionBarTypes";
import { ActionBarSide, ActionButtonDef, getOrderedEnabledActions, splitActionGroups } from "@/components/aac/ActionBarRenderer";
import { useTranslation } from "@/hooks/useTranslation";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";
import { BoardItemEditForm } from "@/components/communication/BoardItemEditForm";
import {
  CommunicationBoardData,
  BoardCell,
  CategoryCell,
  UtteranceCell,
  loadBoards,
  getDefaultBoard,
  resetToDefaultBoard,
  getSentenceBuilderBoard,
  saveBoard,
  updateCategory,
  deleteCategory,
  addCategory,
  updateUtterance,
  deleteUtterance,
  addUtterance,
  reorderCategory,
  reorderUtterance,
  updateCell,
  deleteCell,
} from "@/lib/communicationBoardTypes";
import { GripVertical } from "lucide-react";
import { useCaregiverContacts } from "@/hooks/useCaregiverContacts";

const MAX_UNDO_HISTORY = 20;

export default function CommunicationBoard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t, dir, isRTL, lang } = useTranslation();
  const { t: tCaregiver, isRTL: caregiverRTL } = useCaregiverTranslation();
  
  const [boards, setBoards] = useState<CommunicationBoardData[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<CommunicationBoardData | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<CategoryCell | null>(null);
  
  // Edit mode state
  const isEditModeFromUrl = searchParams.get('edit') === 'true';
  const [isEditMode, setIsEditMode] = useState(isEditModeFromUrl);
  const [editingItem, setEditingItem] = useState<{ type: 'category' | 'utterance'; item: CategoryCell | UtteranceCell; categoryId?: string } | null>(null);
  const [isAddingNew, setIsAddingNew] = useState<{ type: 'category' | 'utterance'; categoryId?: string } | null>(null);
  
  // Drag state for categories
  const [draggedPosition, setDraggedPosition] = useState<{ row: number; col: number } | null>(null);
  
  // Drag state for utterances
  const [draggedUtteranceIndex, setDraggedUtteranceIndex] = useState<number | null>(null);
  
  // Undo history
  const [undoHistory, setUndoHistory] = useState<CommunicationBoardData[]>([]);
  const isUndoing = useRef(false);
  
  // Text bar - accumulates selected messages
  const [messageText, setMessageText] = useState<string>("");
  
  // Action bar config - loaded from editor settings
  const [actionBarConfig, setActionBarConfig] = useState<CommunicationActionBarConfig>(() => loadCommunicationActionBarConfig());
  
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "communicationActionBarConfig_v1") setActionBarConfig(loadCommunicationActionBarConfig());
    };
    const handleFocus = () => setActionBarConfig(loadCommunicationActionBarConfig());
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);
  
  const isActionEnabled = useCallback((id: string) => {
    const item = actionBarConfig.actionItems.find(i => i.id === id);
    return item ? item.enabled : true;
  }, [actionBarConfig]);

  // Pagination for inner category screens - dynamic based on viewport
  const [categoryPage, setCategoryPage] = useState<number>(0);
  
  // Dynamic rows per page based on viewport height
  // On smaller screens (laptop at 100%), use 2 rows; on larger screens use 3
  const [rowsPerPage, setRowsPerPage] = useState(3);
  const MAX_CATEGORY_COLS = 6;
  const MIN_CATEGORY_COLS = 3;
  
  // Detect viewport height and adjust rows per page
  useEffect(() => {
    const checkViewportHeight = () => {
      // If available content height is less than ~500px, use 2 rows
      const vh = window.innerHeight;
      // Account for header (~80px), text bar (~100px), category title (~50px), padding (~50px)
      const availableHeight = vh - 280;
      // Each row needs at least ~120px on small screens
      const maxRows = Math.floor(availableHeight / 100);
      setRowsPerPage(Math.max(2, Math.min(3, maxRows)));
    };
    
    checkViewportHeight();
    window.addEventListener('resize', checkViewportHeight);
    return () => window.removeEventListener('resize', checkViewportHeight);
  }, []);

  // Keep all cards uniform by adapting columns/rows to the widest text in the category
  const getCategoryGridConfig = useCallback((category: CategoryCell | null) => {
    const baseRows = rowsPerPage;

    if (!category) {
      return {
        cols: MAX_CATEGORY_COLS,
        rows: baseRows,
        itemsPerPage: MAX_CATEGORY_COLS * baseRows,
      };
    }

    const utteranceTexts = category.utterances
      .map((u) => (u.text || '').trim())
      .filter(Boolean);

    const longestTextLength = utteranceTexts.reduce((max, text) => Math.max(max, text.length), 0);
    const longestTokenLength = utteranceTexts.reduce((max, text) => {
      const tokenMax = text
        .split(/[\s\-_/]+/)
        .reduce((tokenLongest, token) => Math.max(tokenLongest, token.length), 0);
      return Math.max(max, tokenMax);
    }, 0);

    let cols = MAX_CATEGORY_COLS;
    if (longestTokenLength >= 11 || longestTextLength >= 18) cols = 5;
    if (longestTokenLength >= 14 || longestTextLength >= 24) cols = 4;
    if (longestTokenLength >= 18 || longestTextLength >= 34) cols = 3;

    cols = Math.max(MIN_CATEGORY_COLS, Math.min(MAX_CATEGORY_COLS, cols));

    // Drop to 2 rows only when text is genuinely too long for a 3-row layout
    const rows = (longestTextLength >= 28 || longestTokenLength >= 16)
      ? Math.min(baseRows, 2)
      : baseRows;

    return {
      cols,
      rows,
      itemsPerPage: cols * rows,
    };
  }, [rowsPerPage]);
  
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Check if current board is the sentence builder
  const isSentenceBuilder = selectedBoard?.id === "sentence-builder";

  const { inputMethod } = useInputMethod();
  const isHeadTracking = inputMethod === InputMethod.HEAD_TRACKING || inputMethod === InputMethod.GAZE_TRACKING;
  const userSettings = getUserSettings();
  const userGender = userSettings.gender;
  const isHebrew = lang === "he" || lang === "ar";

  // Multi-language labels for action bar buttons
  const ACTION_LABELS: Record<string, Record<string, string>> = {
    deleteWord: { he: "מחק מילה", en: "Del Word", ru: "Удал. слово", ar: "حذف كلمة" },
    speak:      { he: "דיבור", en: "Speak", ru: "Озвучить", ar: "تحدث" },
    clear:      { he: "נקה", en: "Clear", ru: "Очистить", ar: "مسح" },
    keyboard:   { he: "מקלדת", en: "Keyboard", ru: "Клавиатура", ar: "لوحة مفاتيح" },
    send:       { he: "שליחה", en: "Send", ru: "Отправить", ar: "إرسال" },
    placeholder:{ he: "בחרו מילים מהלוח...", en: "Select words from board...", ru: "Выберите слова с доски...", ar: "اختر كلمات من اللوحة..." },
  };
  const actionLabel = (key: string) => ACTION_LABELS[key]?.[lang] || ACTION_LABELS[key]?.en || key;

  // Load boards on mount and when language or gender changes
  useEffect(() => {
    // Map lang to supported language type
    const boardLang = (lang === "en" || lang === "ru" || lang === "ar") ? lang : "he";
    const genderSetting = userGender === "female" ? "female" : userGender === "male" ? "male" : "neutral";
    const loadedBoards = loadBoards(boardLang, genderSetting);
    setBoards(loadedBoards);
    if (loadedBoards.length > 0) {
      setSelectedBoard(loadedBoards[0]);
    }
    setSelectedCategory(null);
    setMessageText("");
  }, [lang, userGender]);

  // Handle receiving text from Keyboard page
  useEffect(() => {
    if (location.state?.text) {
      const incoming = location.state.text;
      // Auto-add trailing space so next word doesn't merge with previous
      setMessageText(incoming && !incoming.endsWith(" ") ? incoming + " " : incoming);
      // Clear the state to prevent re-applying on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Save board changes to localStorage
  useEffect(() => {
    if (selectedBoard && !isUndoing.current) {
      const boardLang = (lang === "en" || lang === "ru" || lang === "ar") ? lang : "he";
      saveBoard(selectedBoard, boardLang);
      // Update boards list
      setBoards(prev => prev.map(b => b.id === selectedBoard.id ? selectedBoard : b));
    }
  }, [selectedBoard, lang]);

  // Save to undo history before making changes
  const saveToHistory = useCallback(() => {
    if (!selectedBoard) return;
    setUndoHistory(prev => {
      const newHistory = [...prev, selectedBoard];
      if (newHistory.length > MAX_UNDO_HISTORY) {
        return newHistory.slice(-MAX_UNDO_HISTORY);
      }
      return newHistory;
    });
  }, [selectedBoard]);

  // Undo last action
  const handleUndo = useCallback(() => {
    if (undoHistory.length === 0) return;
    
    isUndoing.current = true;
    const previousState = undoHistory[undoHistory.length - 1];
    setUndoHistory(prev => prev.slice(0, -1));
    setSelectedBoard(previousState);
    saveBoard(previousState);
    
    setTimeout(() => {
      isUndoing.current = false;
    }, 0);
  }, [undoHistory]);

  // Calculate effective grid dimensions (filtering empty rows/columns)
  const getEffectiveGridDimensions = useCallback(() => {
    if (!selectedBoard) return { rows: 0, columns: 0, nonEmptyRows: [] as { row: BoardCell[]; originalRowIdx: number }[] };
    
    // Filter out completely empty rows
    const nonEmptyRows = selectedBoard.cells
      .map((row, originalRowIdx) => ({ row, originalRowIdx }))
      .filter(({ row }) => row.some(cell => cell !== null));
    
    // Calculate max columns needed (find the last non-null cell position in any row)
    const effectiveColumns = Math.max(
      1,
      ...nonEmptyRows.map(({ row }) => {
        let lastNonNull = 0;
        row.forEach((cell, idx) => {
          if (cell !== null) lastNonNull = idx + 1;
        });
        return lastNonNull;
      })
    );
    
    return { 
      rows: nonEmptyRows.length, 
      columns: effectiveColumns,
      nonEmptyRows
    };
  }, [selectedBoard]);
  
  // Dynamic category pagination config (depends on widest text in current inner board)
  const categoryGridConfig = getCategoryGridConfig(selectedCategory);
  const ITEMS_PER_PAGE = categoryGridConfig.itemsPerPage;

  // Handle cell click with row-based navigation logic
  // Row 0 (Purple - pronouns): Add word → focus row 1
  // Row 1 (Teal - responses): Add word → focus row 2
  // Row 2 (Green - verbs): Add word AND open category
  // Row 3 (Gray - connectors): Add word → focus row 0
  // Row 4 (Yellow - topics): Open category WITHOUT adding word (just title)
  const handleCellClick = (cell: BoardCell, rowIndex?: number) => {
    if (!cell) return;
    
    // Special handling for sentence builder link
    if (cell.type === "category" && cell.id === "sentence-builder-link") {
      const sentenceBuilderBoard = getSentenceBuilderBoard(lang === "en" ? "en" : "he", userGender);
      setSelectedBoard(sentenceBuilderBoard);
      return;
    }

    // In edit mode, always enter category to edit content
    if (isEditMode && cell.type === "category") {
      setSelectedCategory(cell);
      return;
    }

    // Navigation logic based on row color
    if (cell.type === "category") {
      // Categories: Green opens with word, Yellow opens without word
      if (cell.color === 'green') {
        // Green (verbs): Add category name to sentence AND open inner category
        setMessageText(prev => prev ? `${prev} ${cell.name}` : cell.name);
      }
      // Open the category (all colors)
      setSelectedCategory(cell);
      setCategoryPage(0); // Reset pagination when entering category
    } else {
      // Utterance cells: Add word
      setMessageText(prev => prev ? `${prev} ${cell.text}` : cell.text);
    }
  };

  // Handle utterance click in edit mode
  const handleUtteranceClick = (utterance: UtteranceCell, categoryId: string) => {
    if (isEditMode) {
      setEditingItem({ type: 'utterance', item: utterance, categoryId });
    } else {
      handleSelectItem(utterance.text);
    }
  };

  // Handle word/phrase selection from inner category screens
  const handleSelectItem = (text: string) => {
    setMessageText(prev => prev ? `${prev} ${text}` : text);

    if (selectedCategory && !isSentenceBuilder) {
      // Yellow categories (except phrases): stay in the inner board after selection
      // Green categories and phrases: return to main board after selection
      const stayOpen = selectedCategory.color === 'yellow' && selectedCategory.id !== 'cat-phrases';
      if (!stayOpen) {
        // Close category and return to main board
        setSelectedCategory(null);
      }
    }
  };

  // Text bar actions - with gender-aware TTS for Hebrew "רוצה"
  const handleSpeak = () => {
    if (messageText) {
      let textToSpeak = messageText;
      // Apply gender-aware phonetic override for "רוצה" in Hebrew
      if (lang === "he" && messageText.includes("רוצה")) {
        const isFemale = userGender === "female";
        // Replace "רוצה" with niqqud version for correct pronunciation
        textToSpeak = messageText.replace(/רוצה/g, isFemale ? "רוצָה" : "רוצֶה");
      }
      speak({
        text: textToSpeak,
        onEnd: () => {
          // Clear text after speech ends
          setMessageText("");
        },
      });
    }
    // Stay on the same screen - no navigation change needed
  };

  // Check if there are registered caregivers
  const { hasContacts: hasPhoneContacts } = useCaregiverContacts();
  const canSend = messageText.trim().length > 0 && hasPhoneContacts;

  const handleSend = () => {
    if (messageText.trim() && hasPhoneContacts) {
      sessionStorage.setItem("sendMessageText", messageText);
      navigate("/aac-keyboard/send-flow", { state: { text: messageText } });
    }
  };

  const handleDeleteWord = () => {
    const trimmed = messageText.trimEnd();
    const lastSpaceIndex = trimmed.lastIndexOf(" ");
    if (lastSpaceIndex >= 0) {
      setMessageText(trimmed.substring(0, lastSpaceIndex));
    } else {
      setMessageText("");
    }
  };

  const handleClear = () => {
    setMessageText("");
    stopSpeaking();
    // Stay on the same screen - only clear text, no navigation change
    // Do NOT navigate or change selectedCategory/selectedBoard
  };

  // Navigate to keyboard with current text preserved
  const handleGoToKeyboard = () => {
    if (messageText.trim()) {
      navigate("/aac-keyboard", { state: { text: messageText } });
    }
  };

  // Build ordered action button definitions from config
  const commActionButtonDefs = React.useMemo((): ActionButtonDef[] => {
    const orderedIds = getOrderedEnabledActions(actionBarConfig.actionItems);
    
    const defsMap: Record<string, () => ActionButtonDef> = {
      deleteWord: () => ({ id: "deleteWord", icon: Delete, label: actionLabel("deleteWord"), onClick: handleDeleteWord, disabled: !messageText, variant: "default", iconClassName: "text-destructive" }),
      speak: () => ({ id: "speak", icon: Volume2, label: actionLabel("speak"), onClick: handleSpeak, disabled: !messageText.trim(), variant: "primary" }),
      clear: () => ({ id: "clear", icon: Trash2, label: actionLabel("clear"), onClick: handleClear, disabled: !messageText, variant: "destructive" }),
      pageToggle: () => ({ id: "pageToggle", icon: Keyboard, label: actionLabel("keyboard"), onClick: handleGoToKeyboard, disabled: !messageText.trim(), variant: "default" }),
      send: () => ({ id: "send", icon: Mail, label: actionLabel("send"), onClick: handleSend, disabled: !canSend, variant: "default" }),
    };
    
    return orderedIds
      .map(id => defsMap[id]?.())
      .filter(Boolean) as ActionButtonDef[];
  }, [actionBarConfig, messageText, canSend, lang]);
  
  const { primarySide: commPrimaryActions, secondarySide: commSecondaryActions } = React.useMemo(
    () => splitActionGroups(commActionButtonDefs.map(d => d.id)),
    [commActionButtonDefs]
  );
  
  const commPrimaryDefs = commActionButtonDefs.filter(d => commPrimaryActions.includes(d.id));
  const commSecondaryDefs = commActionButtonDefs.filter(d => commSecondaryActions.includes(d.id));

  const startScrolling = useCallback((direction: "up" | "down") => {
    if (scrollIntervalRef.current) return;
    const scrollAmount = direction === "up" ? -30 : 30;
    scrollIntervalRef.current = setInterval(() => {
      scrollContainerRef.current?.scrollBy({ top: scrollAmount, behavior: "auto" });
    }, 50);
  }, []);

  const stopScrolling = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };
  }, []);

  // Edit mode handlers
  const handleDeleteCell = (cellId: string) => {
    if (!selectedBoard) return;
    saveToHistory();
    const updatedBoard = deleteCell(selectedBoard, cellId);
    setSelectedBoard(updatedBoard);
  };

  const handleDeleteUtterance = (categoryId: string, utteranceId: string) => {
    if (!selectedBoard) return;
    saveToHistory();
    const updatedBoard = deleteUtterance(selectedBoard, categoryId, utteranceId);
    setSelectedBoard(updatedBoard);
    // Update selected category if we're inside one
    if (selectedCategory && selectedCategory.id === categoryId) {
      const updatedCategory = updatedBoard.cells.flat().find(c => c?.type === 'category' && c.id === categoryId) as CategoryCell | undefined;
      setSelectedCategory(updatedCategory || null);
    }
  };

  const handleSaveEdit = (data: { name: string; emoji: string; color: string; showEmoji: boolean; iconName?: string; iconMode?: string }) => {
    if (!selectedBoard || !editingItem) return;
    saveToHistory();

    // Feature flag note: visual toggle is currently disabled in the editor UI.
    // Resolve visibility from actual selected visual content to avoid "empty hole" cards.
    const hasSelectedVisual = data.iconMode === "lucide"
      ? Boolean(data.iconName)
      : Boolean(data.emoji);
    const resolvedShowEmoji = hasSelectedVisual ? true : data.showEmoji;

    // Build the updates object – when in lucide mode, persist the icon name;
    // when in emoji mode, explicitly clear the icon field so the old value
    // doesn't linger after switching from icon → emoji.
    const iconValue = data.iconMode === "lucide" ? (data.iconName || undefined) : undefined;
    const emojiValue = data.iconMode === "lucide" ? "" : data.emoji;

    // Check if this is a main board cell (category or utterance without a parent category)
    const isMainBoardCell = !editingItem.categoryId;

    if (isMainBoardCell) {
      // Use updateCell for all main board cells (both categories and utterances)
      const updatedBoard = updateCell(selectedBoard, editingItem.item.id, {
        name: data.name,
        text: data.name,
        emoji: emojiValue,
        color: data.color,
        showEmoji: resolvedShowEmoji,
        icon: iconValue,
      });
      setSelectedBoard(updatedBoard);
    } else {
      // Utterance inside a category
      const updates = {
        text: data.name,
        emoji: emojiValue,
        color: data.color,
        showEmoji: resolvedShowEmoji,
        icon: iconValue,
      };
      const updatedBoard = updateUtterance(selectedBoard, editingItem.categoryId!, editingItem.item.id, updates);
      setSelectedBoard(updatedBoard);
      // Update selected category
      const updatedCategory = updatedBoard.cells.flat().find(c => c?.type === 'category' && c.id === editingItem.categoryId) as CategoryCell | undefined;
      setSelectedCategory(updatedCategory || null);
    }

    setEditingItem(null);
  };

  const handleAddNew = (data: { name: string; emoji: string; color: string; showEmoji: boolean; iconName?: string; iconMode?: string }) => {
    if (!selectedBoard || !isAddingNew) return;
    saveToHistory();
    
    if (isAddingNew.type === 'category') {
      const newCategory: CategoryCell = {
        id: `cat-${Date.now()}`,
        type: 'category',
        name: data.name,
        emoji: data.iconMode === "lucide" ? "" : data.emoji,
        color: data.color,
        showEmoji: data.showEmoji,
        utterances: [],
        icon: data.iconMode === "lucide" ? data.iconName : undefined,
      };
      const updatedBoard = addCategory(selectedBoard, newCategory);
      setSelectedBoard(updatedBoard);
    } else if (isAddingNew.categoryId) {
      const newUtterance: UtteranceCell = {
        id: `utt-${Date.now()}`,
        type: 'utterance',
        text: data.name,
        emoji: data.iconMode === "lucide" ? "" : data.emoji,
        color: data.color,
        showEmoji: data.showEmoji,
        icon: data.iconMode === "lucide" ? data.iconName : undefined,
      };
      const updatedBoard = addUtterance(selectedBoard, isAddingNew.categoryId, newUtterance);
      setSelectedBoard(updatedBoard);
      // Update selected category
      const updatedCategory = updatedBoard.cells.flat().find(c => c?.type === 'category' && c.id === isAddingNew.categoryId) as CategoryCell | undefined;
      setSelectedCategory(updatedCategory || null);
    }
    
    setIsAddingNew(null);
  };

  const handleResetToDefault = () => {
    saveToHistory();
    // Pass user gender to get gender-appropriate defaults
    const gender = userGender === "female" ? "female" : userGender === "male" ? "male" : "neutral";
    const userLang = (["he", "en", "ru", "ar"] as const).includes(lang as any) ? (lang as "he" | "en" | "ru" | "ar") : "he";
    const defaultBoards = resetToDefaultBoard(userLang, gender);
    setBoards(defaultBoards);
    setSelectedBoard(defaultBoards[0]);
    setSelectedCategory(null);
  };

  // Drag and drop handlers for categories
  const handleCategoryDragStart = (row: number, col: number) => {
    setDraggedPosition({ row, col });
  };

  const handleCategoryDragOver = (e: React.DragEvent, row: number, col: number) => {
    e.preventDefault();
    if (!draggedPosition || !selectedBoard) return;
    if (draggedPosition.row === row && draggedPosition.col === col) return;
    
    saveToHistory();
    const updatedBoard = reorderCategory(selectedBoard, draggedPosition.row, draggedPosition.col, row, col);
    setSelectedBoard(updatedBoard);
    setDraggedPosition({ row, col });
  };

  const handleCategoryDragEnd = () => {
    setDraggedPosition(null);
  };

  // Drag and drop handlers for utterances
  const handleUtteranceDragStart = (index: number) => {
    setDraggedUtteranceIndex(index);
  };

  const handleUtteranceDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedUtteranceIndex === null || !selectedBoard || !selectedCategory) return;
    if (draggedUtteranceIndex === index) return;
    
    saveToHistory();
    const updatedBoard = reorderUtterance(selectedBoard, selectedCategory.id, draggedUtteranceIndex, index);
    setSelectedBoard(updatedBoard);
    
    // Update selected category to reflect the new order
    const updatedCategory = updatedBoard.cells.flat().find(
      c => c?.type === 'category' && c.id === selectedCategory.id
    ) as CategoryCell | undefined;
    if (updatedCategory) {
      setSelectedCategory(updatedCategory);
    }
    
    setDraggedUtteranceIndex(index);
  };

  const handleUtteranceDragEnd = () => {
    setDraggedUtteranceIndex(null);
  };

  // Handle back navigation
  const handleBack = useCallback(() => {
    // If in edit form, close it first
    if (editingItem) {
      setEditingItem(null);
      return;
    }
    if (isAddingNew) {
      setIsAddingNew(null);
      return;
    }
    // If inside a category, go back to main board
    if (selectedCategory) {
      setSelectedCategory(null);
      return;
    }
    // If on sentence builder, go back to default board
    if (selectedBoard?.id === "sentence-builder") {
      const defaultBoard = boards.find(b => b.id === "default") || boards[0];
      if (defaultBoard) {
        setSelectedBoard(defaultBoard);
      }
      return;
    }
    // Otherwise go back in browser history
    navigate(-1);
  }, [editingItem, isAddingNew, selectedCategory, selectedBoard, boards, navigate]);

  // Render a cell - text only, no emoji for main board
  const renderCell = (cell: BoardCell, index: number, rowIdx: number, colIdx: number) => {
    // Empty cell - only show plus icon in edit mode
    if (!cell) {
      if (isEditMode) {
        return (
          <button
            onClick={() => setIsAddingNew({ type: 'category' })}
            onDragOver={(e) => {
              e.preventDefault();
              handleCategoryDragOver(e, rowIdx, colIdx);
            }}
            onDrop={(e) => e.preventDefault()}
            className={cn(
              "w-full h-full flex items-center justify-center rounded-xl",
              "border-2 border-dashed border-muted-foreground/30 bg-muted/10",
              "hover:border-primary/50 hover:bg-primary/5 transition-all duration-200",
              "cursor-pointer"
            )}
          >
            <Plus className="w-8 h-8 text-muted-foreground/50 hover:text-primary/70" />
          </button>
        );
      }
      // In regular mode, empty cells are invisible placeholders that maintain grid structure
      return <div className="w-full h-full" />;
    }

    const colorClasses = getColorClasses(cell.color);
    const displayText = cell.type === "category" ? cell.name : cell.text;

    const isDragging = isEditMode && draggedPosition?.row === rowIdx && draggedPosition?.col === colIdx;

    // Check if this is a category with content (utterances)
    const hasContent = cell.type === 'category' && cell.utterances && cell.utterances.length > 0;

    return (
      <motion.div
        draggable={isEditMode}
        onDragStart={isEditMode ? () => handleCategoryDragStart(rowIdx, colIdx) : undefined}
        onDragOver={isEditMode ? (e) => handleCategoryDragOver(e, rowIdx, colIdx) : undefined}
        onDragEnd={isEditMode ? handleCategoryDragEnd : undefined}
        onClick={() => handleCellClick(cell)}
        whileHover={isEditMode ? undefined : { scale: 1.02 }}
        whileTap={isEditMode ? undefined : { scale: 0.98 }}
        data-scan-item
        className={cn(
          "relative w-full h-full flex items-center justify-center rounded-xl",
          "border transition-all duration-200",
          "backdrop-blur-sm",
          colorClasses.bg,
          colorClasses.border,
          !isEditMode && "hover:shadow-medium cursor-pointer",
          isEditMode && "cursor-move",
          isDragging && "opacity-50 scale-95",
        )}
        style={{
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
        }}
      >
        {isEditMode && (
          <>
            {/* Drag handle */}
            <div className="absolute top-1 left-1/2 -translate-x-1/2">
              <GripVertical className="w-4 h-4 text-muted-foreground" />
            </div>
            
            {/* Edit cell properties button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                // For both categories and utterances on main board, set appropriate type
                setEditingItem({ 
                  type: cell.type === 'category' ? 'category' : 'utterance', 
                  item: cell as CategoryCell | UtteranceCell 
                });
              }}
              className={cn(
                "absolute top-1 p-1.5 bg-primary rounded-full hover:bg-primary/80 z-10",
                isRTL ? "right-1" : "left-1"
              )}
            >
              <Pencil className="w-3 h-3 text-primary-foreground" />
            </button>
            
            {/* Delete button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteCell(cell.id);
              }}
              className={cn(
                "absolute top-1 p-1.5 bg-destructive rounded-full hover:bg-destructive/80 z-10",
                isRTL ? "left-1" : "right-1"
              )}
            >
              <Trash2 className="w-3 h-3 text-destructive-foreground" />
            </button>
            
            {/* Edit content button - only for categories with utterances */}
            {hasContent && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCategory(cell as CategoryCell);
                }}
                className={cn(
                  "absolute bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-md",
                  "bg-secondary/90 hover:bg-secondary text-secondary-foreground",
                  "text-xs font-medium transition-colors z-10",
                  "flex items-center gap-0.5"
                )}
              >
                {isRTL ? "תוכן" : "Edit"}
                <ChevronDown className={cn("w-3 h-3", isRTL ? "rotate-90" : "-rotate-90")} />
              </button>
            )}
          </>
        )}
        
        {/* Text with optional chevron for yellow categories */}
        <div className="flex items-center gap-1">
          <span className={cn(
            "font-bold text-center leading-tight",
            colorClasses.text,
            "text-base sm:text-lg md:text-xl lg:text-2xl"
          )}>
            {displayText}
          </span>
          
          {/* Chevron after the word, pointing in continuation direction */}
          {!isEditMode && cell.type === 'category' && cell.utterances && cell.utterances.length > 0 && (
            isRTL ? (
              <ChevronLeft className={cn("w-4 h-4 sm:w-5 sm:h-5", colorClasses.text, "opacity-70 flex-shrink-0")} />
            ) : (
              <ChevronRight className={cn("w-4 h-4 sm:w-5 sm:h-5", colorClasses.text, "opacity-70 flex-shrink-0")} />
            )
          )}
        </div>
      </motion.div>
    );
  };

  // Render utterance button
  const renderUtterance = (utterance: UtteranceCell, index: number, categoryId: string) => {
    const shouldShowEmoji = utterance.showEmoji !== false || Boolean(utterance.icon);
    const isDraggingUtterance = isEditMode && draggedUtteranceIndex === index;

    // Determine which sub-board row CSS variables to use based on parent category
    const parentColor = selectedCategory?.color;
    const subRow = parentColor === 'green' ? '3' : parentColor === 'yellow' ? '5' : '3';
    const subVarPrefix = `--design-row${subRow}-sub`;
    const renderedIcon = utterance.icon
      ? renderIconByName(utterance.icon, "w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 flex-shrink-0")
      : null;

    return (
      <motion.div
        key={utterance.id}
        draggable={isEditMode}
        onDragStart={isEditMode ? () => handleUtteranceDragStart(index) : undefined}
        onDragOver={isEditMode ? (e) => handleUtteranceDragOver(e, index) : undefined}
        onDragEnd={isEditMode ? handleUtteranceDragEnd : undefined}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={isEditMode ? undefined : { scale: 1.08, y: -4 }}
        whileTap={isEditMode ? undefined : { scale: 0.92 }}
        transition={{ delay: index * 0.03 }}
        onClick={() => handleUtteranceClick(utterance, categoryId)}
        data-scan-item
        className={cn(
          "comm-utterance-card relative w-full h-full min-w-0 min-h-0 flex flex-col items-center justify-center overflow-hidden",
          "border transition-all duration-200",
          "backdrop-blur-sm",
          "gap-1 p-2 rounded-xl",
          !isEditMode && "hover:shadow-medium cursor-pointer",
          isEditMode && "cursor-move",
          isDraggingUtterance && "opacity-50 scale-95",
        )}
        style={{
          backgroundColor: `hsl(var(${subVarPrefix}-bg))`,
          borderColor: `hsl(var(${subVarPrefix}-border))`,
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
        }}
      >
        {isEditMode && (
          <>
            {/* Drag handle */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2">
              <GripVertical className="w-5 h-5 text-muted-foreground" />
            </div>

            {/* Edit button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setEditingItem({ type: 'utterance', item: utterance, categoryId });
              }}
              className={cn(
                "absolute top-3 p-2 bg-primary rounded-full hover:bg-primary/80 z-10",
                isRTL ? "right-3" : "left-3"
              )}
            >
              <Pencil className="w-5 h-5 text-primary-foreground" />
            </button>

            {/* Delete button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteUtterance(categoryId, utterance.id);
              }}
              className={cn(
                "absolute top-3 p-2 bg-destructive rounded-full hover:bg-destructive/80 z-10",
                isRTL ? "left-3" : "right-3"
              )}
            >
              <Trash2 className="w-5 h-5 text-destructive-foreground" />
            </button>
          </>
        )}

        {/* Emoji/Icon area - fixed height allocation to keep cards uniform */}
        {shouldShowEmoji && (renderedIcon || (utterance.emoji && utterance.emoji.trim() !== "")) && (
          <div className="flex items-center justify-center flex-shrink-0" style={{ height: 'clamp(2.25rem, 6vh, 4rem)' }}>
            {renderedIcon ? (
              <span style={{ color: `hsl(var(${subVarPrefix}-icon))` }}>
                {renderedIcon}
              </span>
            ) : (
              utterance.emoji && (
                utterance.emoji.startsWith("data:image")
                  ? <img
                      src={utterance.emoji}
                      alt=""
                      className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 flex-shrink-0 object-contain"
                      style={{ maxHeight: '100%' }}
                    />
                  : <span
                      className="flex-shrink-0"
                      style={{
                        fontSize: 'clamp(2.25rem, 6vh, 4rem)',
                        lineHeight: 1,
                      }}
                    >
                      {utterance.emoji}
                    </span>
              )
            )}
          </div>
        )}
        {/* Text area - wraps naturally while preserving uniform card sizing */}
        <bdi
          dir={isRTL ? "rtl" : "ltr"}
          className="font-bold text-center leading-tight w-full min-h-0 min-w-0 flex-shrink-0 break-words"
          style={{
            color: `hsl(var(${subVarPrefix}-text))`,
            unicodeBidi: "plaintext",
            overflowWrap: 'anywhere',
            fontSize: 'clamp(0.9em, 2.5vh, 1.25em)',
          }}
        >
          {utterance.text}
        </bdi>
      </motion.div>
    );
  };

  // Show edit form if editing
  if (editingItem) {
    // Only utterances inside a category get visual elements.
    // Categories and top-level items on the main board = text + color only.
    const isInnerUtterance = editingItem.type === 'utterance' && Boolean(editingItem.categoryId);
    const initialShowEmoji = isInnerUtterance;

    return (
      <BoardItemEditForm
        type={editingItem.type}
        initialName={editingItem.type === 'category' ? (editingItem.item as CategoryCell).name : (editingItem.item as UtteranceCell).text}
        initialEmoji={editingItem.item.emoji || ""}
        initialColor={editingItem.item.color}
        initialShowEmoji={initialShowEmoji}
        initialIconName={editingItem.item.icon || "MessageSquare"}
        iconMode={editingItem.item.icon ? "lucide" : "emoji"}
        hideVisualElement={!isInnerUtterance}
        onSave={handleSaveEdit}
        onCancel={() => setEditingItem(null)}
      />
    );
  }

  // Show add form if adding new
  if (isAddingNew) {
    // Only utterances inside a category get visual elements.
    const isInnerUtterance = isAddingNew.type === 'utterance' && Boolean(isAddingNew.categoryId);

    return (
      <BoardItemEditForm
        type={isAddingNew.type}
        initialName=""
        initialEmoji="📝"
        initialColor="teal"
        initialShowEmoji={isInnerUtterance}
        initialIconName="MessageSquare"
        iconMode="emoji"
        hideVisualElement={!isInnerUtterance}
        onSave={handleAddNew}
        onCancel={() => setIsAddingNew(null)}
        isNew
      />
    );
  }


  return (
    <MainLayout
      onBack={handleBack}
    >
      <div className="flex-1 flex flex-col overflow-hidden" dir={dir}>
        {/* Edit Mode Header */}
        {isEditMode && (
          <div className="flex items-center justify-between px-4 py-3 bg-card/50 border-b border-border">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-foreground">
                {tCaregiver("editCommunicationBoard")}
              </h1>
              
              {/* Edit Action Bar button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/communication-action-bar-editor')}
              >
                <Settings2 className="w-4 h-4 mr-2" />
                {tCaregiver("editTopActionBar" as any)}
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleUndo}
                disabled={undoHistory.length === 0}
              >
                <Undo2 className="w-4 h-4 mr-2" />
                {tCaregiver("undo")}
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  if (selectedCategory) {
                    setIsAddingNew({ type: 'utterance', categoryId: selectedCategory.id });
                  } else {
                    setIsAddingNew({ type: 'category' });
                  }
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                {selectedCategory 
                  ? tCaregiver("addCard")
                  : tCaregiver("addCategory" as any)
                }
              </Button>
              
              <Button variant="outline" size="sm" onClick={handleResetToDefault}>
                <RotateCcw className="w-4 h-4 mr-2" />
                {tCaregiver("resetToDefault")}
              </Button>
              
              <Button size="sm" disabled={undoHistory.length === 0} onClick={() => {
                setIsEditMode(false);
                setUndoHistory([]);
                navigate('/board-editor');
              }}>
                <Check className="w-4 h-4 mr-2" />
                {tCaregiver("doneEditing")}
              </Button>
            </div>
          </div>
        )}

        {/* Top Text Bar - hide in edit mode */}
        {!isEditMode && (
          <header className={cn(
            "flex items-stretch gap-2 mb-2 mt-2 px-4 py-2 rounded-xl",
            isHebrew ? "flex-row" : "flex-row-reverse",
            )}>
            {/* Primary side (right in Hebrew, left in English) */}
            <ActionBarSide
              buttons={commPrimaryDefs}
              isRTL={isHebrew}
            />

            {/* Text Display */}
            <div
              className="flex-1 px-4 rounded-xl bg-card border-2 border-border flex items-center overflow-y-auto overflow-x-hidden min-h-[76px]"
              dir={messageText ? (isHebrew ? "rtl" : "ltr") : (isHebrew ? "rtl" : "ltr")}
            >
              <div className="w-full text-start">
                {messageText ? (
                  <span className="text-xl sm:text-2xl font-medium whitespace-pre-wrap break-words leading-relaxed">
                    {messageText}
                  </span>
                ) : (
                  <span className="text-muted-foreground text-base sm:text-lg">
                    {actionLabel("placeholder")}
                  </span>
                )}
              </div>
            </div>

            {/* Secondary side (left in Hebrew, right in English) */}
            <ActionBarSide
              buttons={commSecondaryDefs}
              isRTL={isHebrew}
            />
          </header>
        )}

        {/* Category Title - only for yellow (topic) categories, with page indicator */}
        {selectedCategory && selectedCategory.color === 'yellow' && (
          <div className="px-4 py-2 border-b border-border bg-card/30 flex items-center justify-between">
            <h2 className="text-xl font-bold">{selectedCategory.name}</h2>
            {(() => {
              const { itemsPerPage } = getCategoryGridConfig(selectedCategory);
              const totalPages = Math.ceil(selectedCategory.utterances.length / itemsPerPage);
              if (totalPages <= 1) return null;
              const page = Math.min(categoryPage, totalPages - 1) + 1;
              return (
                <span className="text-sm text-muted-foreground font-medium" dir="ltr">
                  {page} / {totalPages}
                </span>
              );
            })()}
          </div>
        )}

        {/* Page indicator for non-yellow categories (no title header) */}
        {selectedCategory && selectedCategory.color !== 'yellow' && (() => {
          const { itemsPerPage } = getCategoryGridConfig(selectedCategory);
          const totalPages = Math.ceil(selectedCategory.utterances.length / itemsPerPage);
          if (totalPages <= 1) return null;
          const page = Math.min(categoryPage, totalPages - 1) + 1;
          return (
            <div className="px-4 py-1 flex justify-end">
              <span className="text-sm text-muted-foreground font-medium" dir="ltr">
                {page} / {totalPages}
              </span>
            </div>
          );
        })()}

        {/* Main Content */}
        <div ref={scrollContainerRef} className="flex-1 overflow-hidden p-4 relative flex flex-col">
          <AnimatePresence mode="wait">
            {selectedCategory ? (
              // Category Utterances View - with pagination
              <motion.div
                key="utterances"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="h-full w-full flex items-center"
              >
                {(() => {
                  // Dynamic columns and rows based on viewport and longest text in the category
                  const { cols, rows: maxRows, itemsPerPage } = getCategoryGridConfig(selectedCategory);
                  const totalItems = selectedCategory.utterances.length;
                  const totalPages = Math.ceil(totalItems / itemsPerPage);
                  const currentPage = Math.min(categoryPage, totalPages - 1);
                  
                  // Get items for current page
                  const pageStart = currentPage * itemsPerPage;
                  const pageEnd = Math.min(pageStart + itemsPerPage, totalItems);
                  const pageItems = selectedCategory.utterances.slice(pageStart, pageEnd);
                  const pageRowCount = Math.min(Math.ceil(pageItems.length / cols), maxRows);
                  
                  // Pagination buttons (RTL-aware like emoji keyboard)
                  const hasPrevPage = currentPage > 0;
                  const hasNextPage = currentPage < totalPages - 1;
                  
                  // In RTL: left arrow = next, right arrow = prev
                  // In LTR: left arrow = prev, right arrow = next
                  const showLeftArrow = isHebrew ? hasNextPage : hasPrevPage;
                  const showRightArrow = isHebrew ? hasPrevPage : hasNextPage;
                  
                  const handleLeftClick = () => {
                    if (isHebrew) {
                      setCategoryPage(p => Math.min(p + 1, totalPages - 1));
                    } else {
                      setCategoryPage(p => Math.max(p - 1, 0));
                    }
                  };

                  const handleRightClick = () => {
                    if (isHebrew) {
                      setCategoryPage(p => Math.max(p - 1, 0));
                    } else {
                      setCategoryPage(p => Math.min(p + 1, totalPages - 1));
                    }
                  };
                  
                  return (
                    // Use dir="ltr" to ensure consistent arrow positioning regardless of RTL context
                    <div dir="ltr" className="flex items-center w-full h-full gap-3">
                      {/* Left pagination arrow - in Hebrew this is "next" (pointing outward) */}
                      <div className="flex-shrink-0 w-20 h-full flex items-center justify-center" data-scan-row="pagination">
                        {showLeftArrow ? (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={handleLeftClick}
                            data-scan-item
                            className={cn(
                              "h-full w-16 rounded-2xl border-2 bg-card",
                              "border-primary/40",
                              "shadow-sm",
                              "transition-all duration-200",
                            )}
                          >
                            <ChevronLeft style={{ width: 48, height: 48 }} className="text-primary !w-12 !h-12" strokeWidth={3} />
                          </Button>
                        ) : (
                          // Invisible placeholder to maintain layout
                          <div className="w-16" />
                        )}
                      </div>
                      
                      {/* Grid content - use CSS Grid for consistent row heights */}
                      <div 
                        className="flex-1 h-full gap-2"
                        style={{
                          display: 'grid',
                          gridTemplateRows: `repeat(${pageRowCount}, 1fr)`,
                        }}
                      >
                        {Array.from({ length: pageRowCount }).map((_, rowIdx) => {
                          const rowStart = rowIdx * cols;
                          const rowItems = pageItems.slice(rowStart, Math.min(rowStart + cols, pageItems.length));
                          const globalRowStart = rowIdx * cols;

                          return (
                            <div
                              key={`row-${rowIdx}`}
                              data-scan-row={`cat-row-${rowIdx}`}
                              className="relative min-h-0 min-w-0"
                            >
                              <div
                                className="grid gap-2 h-full w-full min-w-0"
                                style={{
                                  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                                }}
                              >
                                {/* In RTL (Hebrew), add empty cells BEFORE items so items align to the right */}
                                {isHebrew && rowItems.length < cols && Array.from({ length: cols - rowItems.length }).map((_, i) => (
                                  <div key={`empty-${i}`} className="h-full w-full min-h-0 min-w-0" />
                                ))}
                                {/* Reverse items for RTL languages since container is dir="ltr" */}
                                {(isHebrew ? [...rowItems].reverse() : rowItems).map((utterance, cellIdx) => {
                                  const actualCellIdx = isHebrew ? (rowItems.length - 1 - cellIdx) : cellIdx;
                                  const globalIndex = globalRowStart + actualCellIdx;
                                  return (
                                    <div key={utterance.id} className="h-full w-full min-h-0 min-w-0">
                                      {renderUtterance(utterance, globalIndex, selectedCategory.id)}
                                    </div>
                                  );
                                })}
                                {/* In LTR, add empty cells AFTER items to maintain grid structure */}
                                {!isHebrew && rowItems.length < cols && Array.from({ length: cols - rowItems.length }).map((_, i) => (
                                  <div key={`empty-${i}`} className="h-full w-full min-h-0 min-w-0" />
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Right pagination arrow - in Hebrew this is "prev" (pointing outward) */}
                      <div className="flex-shrink-0 w-20 h-full flex items-center justify-center" data-scan-row="pagination">
                        {showRightArrow ? (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={handleRightClick}
                            data-scan-item
                            className={cn(
                              "h-full w-16 rounded-2xl border-2 bg-card",
                              "border-primary/40",
                              "shadow-sm",
                              "transition-all duration-200",
                            )}
                          >
                            <ChevronRight style={{ width: 48, height: 48 }} className="text-primary !w-12 !h-12" strokeWidth={3} />
                          </Button>
                        ) : (
                          // Invisible placeholder to maintain layout
                          <div className="w-16" />
                        )}
                      </div>
                    </div>
                  );
                })()}
              </motion.div>
            ) : selectedBoard ? (
              // Board Cells View - Full screen responsive grid without emoji
              <motion.div
                key="board"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="h-full w-full flex flex-col gap-2"
              >
                {(() => {
                  // Use the memoized effective grid dimensions
                  const { columns: effectiveColumns, nonEmptyRows } = getEffectiveGridDimensions();

                  return nonEmptyRows.map(({ row, originalRowIdx }, displayRowIdx) => {
                    // Trim the row to effective columns
                    const trimmedRow = row.slice(0, effectiveColumns);

                    return (
                      <div
                        key={`row-${originalRowIdx}`}
                        data-scan-row={`board-row-${originalRowIdx}`}
                        className="flex-1 min-h-0 relative"
                      >
                        <div
                          className="grid gap-2 h-full w-full"
                          style={{
                            gridTemplateColumns: `repeat(${effectiveColumns}, 1fr)`,
                          }}
                        >
                          {trimmedRow.map((cell, colIndex) => {
                            const flatIndex = displayRowIdx * effectiveColumns + colIndex;
                            return (
                              <div key={`${originalRowIdx}-${colIndex}`} className="h-full w-full min-h-0">
                                {renderCell(cell, flatIndex, originalRowIdx, colIndex)}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
              </motion.div>
            ) : (
              // Board Selection View
              <motion.div
                key="boards"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {boards.map((board, index) => {
                    const filledCells = board.cells.flat().filter((c) => c !== null).length;
                    return (
                      <motion.button
                        key={board.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => setSelectedBoard(board)}
                        data-scan-item
                        className={cn(
                          "flex flex-col items-center justify-center gap-6 p-10 rounded-3xl min-h-[250px]",
                          "bg-card border-3 transition-all duration-200",
                          "active:scale-95",
                          "border-primary/30 hover:border-primary hover:scale-105 hover:shadow-lg"
                        )}
                      >
                        {board.icon && renderIconByName(board.icon, "w-20 h-20 text-primary")}
                        {board.emoji && <span className="text-6xl">{board.emoji}</span>}
                        <span className="text-2xl font-bold text-foreground">{board.name}</span>
                        <span className="text-lg text-muted-foreground">
                          {board.rows}x{board.columns} • {filledCells} {t("items")}
                        </span>
                      </motion.button>
                    );
                  })}

                  {/* Create New Board Button */}
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: boards.length * 0.05 }}
                    onClick={() => navigate("/communication/builder")}
                    data-scan-item
                    className={cn(
                      "flex flex-col items-center justify-center gap-6 p-10 rounded-3xl min-h-[250px]",
                      "border-3 border-dashed border-primary/40 bg-primary/5",
                      "hover:border-primary hover:bg-primary/10 hover:scale-105",
                      "active:scale-95 transition-all duration-200",
                    )}
                  >
                    <Plus className="w-20 h-20 text-primary" />
                    <span className="text-2xl font-bold text-primary">{t("buildNewBoard")}</span>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Floating Scroll Buttons for Head Tracking */}
        {isHeadTracking && !isEditMode && (
          <div className="fixed left-4 top-1/2 -translate-y-1/2 flex flex-col gap-4 z-50">
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onMouseEnter={() => startScrolling("up")}
              onMouseLeave={stopScrolling}
              onClick={() => scrollContainerRef.current?.scrollBy({ top: -200, behavior: "smooth" })}
              className={cn(
                "w-16 h-16 rounded-2xl flex items-center justify-center",
                "bg-card border-2 border-border",
                "hover:border-accent hover:ring-4 hover:ring-accent/30 hover:scale-110",
                "transition-all duration-200 shadow-lg"
              )}
            >
              <ChevronUp className="w-8 h-8 text-primary" />
            </motion.button>
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              onMouseEnter={() => startScrolling("down")}
              onMouseLeave={stopScrolling}
              onClick={() => scrollContainerRef.current?.scrollBy({ top: 200, behavior: "smooth" })}
              className={cn(
                "w-16 h-16 rounded-2xl flex items-center justify-center",
                "bg-card border-2 border-border",
                "hover:border-accent hover:ring-4 hover:ring-accent/30 hover:scale-110",
                "transition-all duration-200 shadow-lg"
              )}
            >
              <ChevronDown className="w-8 h-8 text-primary" />
            </motion.button>
          </div>
        )}

      </div>
    </MainLayout>
  );
}